import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import styles from './css/LeaderDashboard.module.css';
import './css/LeaderDashboard.css';
import { AuthContext } from './AuthContext';
import API_BASE_URL from './config';
import LeaderFeedbackVisualization from './LeaderFeedbackVisualization';

const relationshipTypes = ['Self', 'Manager', 'Peer', 'Direct Report', 'Other'];

const LeaderDashboard = () => {
  const [customer, setCustomer] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('raters');
  const [participantName, setParticipantName] = useState('');
  const [email, setEmail] = useState('');
  const [relationship, setRelationship] = useState(relationshipTypes[0]);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      // The user object from AuthContext already contains the linked customerId!
      if (user && user.customerId) {
        try {
          // *** THE FIX IS HERE ***
          // We already have the customerId. We don't need a special endpoint.
          // We can use the existing admin endpoint because the security check on the
          // backend will confirm this user owns this customerId.
          const customerRes = await axios.get(`${API_BASE_URL}/api/my-report/${user.customerId}`, { withCredentials: true });
          const customerData = customerRes.data.data.customer;
          setCustomer(customerData);

          const participantsRes = await axios.get(`${API_BASE_URL}/api/leader-participants`, {
            withCredentials: true,
            params: { customerId: customerData._id },
          });
          setParticipants(participantsRes.data);

        } catch (err) {
          setError(err.response?.data?.message || 'Failed to load dashboard data.');
        } finally {
          setLoading(false);
        }
      } else {
        setError("Your user account is not linked to a customer profile. Please contact an administrator.");
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [user]);

  const completedCount = useMemo(() => participants.filter(p => p.completed).length, [participants]);
  const totalCount = participants.length;
  const isReportReady = completedCount > 0;

  const handleAddParticipant = async () => {
    if (!email || !participantName) return setError('Please enter all fields.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Please enter a valid email.');
    setError(null);
    try {
      const payload = { customerId: customer._id, entries: [{ email, name: participantName, relationship }] };
      const response = await axios.post(`${API_BASE_URL}/api/generate-feedback-urls`, payload, { withCredentials: true });
      setParticipants(prev => [response.data[0], ...prev]);
      setEmail('');
      setParticipantName('');
      setRelationship(relationshipTypes[0]);
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    if (!window.confirm("Are you sure you want to remove this rater?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/participants/${participantId}`, { withCredentials: true });
      setParticipants(prev => prev.filter(p => p._id !== participantId));
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
    }
  };

  const handleReadyToNotifyChange = async (event) => {
    const isChecked = event.target.checked;
    try {
      await axios.put(`${API_BASE_URL}/api/update-customer-status/${user.id}`, { readyToNotifyParticipants: isChecked }, { withCredentials: true });
      setCustomer(prev => ({ ...prev, readyToNotifyParticipants: isChecked }));
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
    }
  };

  if (loading) return <div>Loading Dashboard...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!customer) return <div className="error-message">Could not load your profile. Please contact support.</div>;

  return (
    <div className="leader-dashboard-container">
      <div className="dashboard-header">
        <h2>{customer.firstName} {customer.lastName}'s Dashboard</h2>
        <p>Product: {customer.product?.productName || 'N/A'}</p>
      </div>
      <div className="dashboard-summary">
        <div className="summary-item">
          <div className="count">{totalCount}</div>
          <div className="label">Total Raters</div>
        </div>
        <div className="summary-item">
          <div className="count">{completedCount}</div>
          <div className="label">Responses Received</div>
        </div>
        <div className="readiness-toggle">
          <label htmlFor="ready-checkbox">Ready to Start?</label>
          <input
            type="checkbox"
            id="ready-checkbox"
            checked={customer.readyToNotifyParticipants || false}
            onChange={handleReadyToNotifyChange}
          />
        </div>
      </div>
      <div className="dashboard-tabs">
        <button className={`tab-button ${activeTab === 'raters' ? 'active' : ''}`} onClick={() => setActiveTab('raters')}>
          Manage Raters
        </button>
        <button className={`tab-button ${activeTab === 'report' ? 'active' : ''}`} onClick={() => setActiveTab('report')}>
          View My Report
        </button>
      </div>
      <div className="tab-content">
        {activeTab === 'raters' && (
          <div>
            <h3>Add a New Rater</h3>
            <div className={styles.inputGroup}>
              <input type="text" value={participantName} onChange={(e) => setParticipantName(e.target.value)} placeholder="Rater's Name" />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Rater's Email" />
              <select value={relationship} onChange={(e) => setRelationship(e.target.value)}>
                {relationshipTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
              <button onClick={handleAddParticipant}>Add Rater</button>
            </div>
            {error && <p className={styles.error}>{error}</p>}
            <h3>Your Rater List</h3>
            <ul className={styles.participantList}>
              {participants.map((p) => (
                <li key={p._id || p.email} className={p.completed ? styles.completed : styles.notCompleted}>
                  <span className={styles.participantDetail}>{p.participantName}</span>
                  <span className={styles.participantDetail}>{p.email}</span>
                  <span className={styles.participantDetail}>{p.relationship}</span>
                  <button className={styles.deleteButton} onClick={() => handleDeleteParticipant(p._id)} disabled={p.completed}>
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {activeTab === 'report' && (
          <div>
            {isReportReady ? (
              <LeaderFeedbackVisualization customerIdForReport={customer._id} />
            ) : (
              <div className="report-not-ready">
                <h3>Your Report is Not Yet Available</h3>
                <p>Once your raters have submitted their feedback, your full 360° Compass Report will appear here.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderDashboard;